import Form from 'antd/es/form';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { ProxyServer, ProxyServerFormFields, ProxyServerFormInputs, ProxyServersQueryKeys, useCreateProxyServersMutation, useFetchSupportedOSProxyServerQuery } from '@/modules/ProxyServers';

import { ProxyServersFormView } from './ProxyServersFormView';
import { proxyServersFormValidationSchema } from './validation';

export const ProxyServersCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ProxyServerFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ProxyServerFormInputs>>(null);

  const hostingIdFormValue = Form.useWatch<string>(ProxyServerFormInputs.HostingId, form);

  const {
    data: supportedOsData,
    isLoading: isSupportedOsLoading,
    isError: isSupportedOsError,
    error: supportedOsError
  } = useFetchSupportedOSProxyServerQuery()();

  const { mutate: createProxyServerMutation, isLoading: createProxyServerIsLoading } = useCreateProxyServersMutation()({
    onSuccess: async (data: ProxyServer) => {
      appMessageApi.success('Проксі-сервер успішно створено!');
      appMessageApi.info('Проксі-сервер додано в чергу на налаштування.');

      await queryClient.refetchQueries([ProxyServersQueryKeys.GetAll]);

      navigate(AppRoutes.ProxyServersById.replace(':proxyServerId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ProxyServerFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ProxyServerFormFields = {
    name: undefined,
    hostingAccountId: undefined,
    hostingId: undefined,
    ips: undefined,
    ip: undefined,
    password: undefined
  };

  const handleFinish = useCallback((values: ProxyServerFormFields): void => {
    setFormErrors(null);
    createProxyServerMutation(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.ProxyServers);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout
      data={ supportedOsData }
      isLoading={ isSupportedOsLoading }
      isError={ isSupportedOsError }
      error={ supportedOsError }
    >
      { (supportedOs) => (
        <ProxyServersFormView
          formName="proxy-server-create"
          formType="create"
          form={ form }
          formErrors={ formErrors }
          initialValues={ initialValues }
          isFormLoading={ createProxyServerIsLoading }
          validationSchema={ proxyServersFormValidationSchema }
          hostingIdFormValue={ hostingIdFormValue }
          supportedOs={ supportedOs }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) }
    </FallbackedLayout>
  );
});
